/**
 * Who has access at the selected scope, with each holder's last sign-in
 * (ORDR-120, FR-STF-003), extracted from `GrantsPanel` to keep both files under
 * the 300-line limit.
 *
 * IT FAILS CLOSED. A roster that cannot be read renders an error INSTEAD of the
 * list. An empty list reads as "nobody has access here", and falling back to
 * `GET /access/grants` would drop the sign-in column while looking like
 * working; both are ruled out, and the panel tests pin it.
 *
 * ONE LINE EXPLAINS THE LIMIT, verbatim from the charter, because the column
 * is only honest if the reader knows it forgets (see `signIn.ts`).
 */

import type { ReactElement } from "react";
import { holderName, roleName } from "./rosterLabels.js";
import { RosterRow } from "./RosterRow.js";
import type { CatalogRole, RosterEntry } from "./types.js";

/** `entries: null` is a failed read, which is not the same as an empty one. */
export interface RosterRead {
  readonly key: string;
  readonly entries: readonly RosterEntry[] | null;
  /** When it was read, so every age on screen is measured from one moment. */
  readonly at: Date;
}

export interface RosterProps {
  /** Undefined while the selected scope's read is in flight. */
  readonly read: RosterRead | undefined;
  readonly ownUserId: string;
  readonly catalog: readonly CatalogRole[];
  readonly busy: boolean;
  readonly onRevoke: (entry: RosterEntry) => void;
}

export function Roster({ read, ownUserId, catalog, busy, onRevoke }: RosterProps): ReactElement {
  if (read === undefined) {
    return <p className="text-sm">Loading who has access here...</p>;
  }
  if (read.entries === null) {
    return (
      <p role="alert" data-testid="roster-error" className="text-sm">
        Could not load who has access here, so nobody is listed. Try again before
        changing anyone&apos;s access.
      </p>
    );
  }
  return (
    <>
      <ul data-testid="grant-list" className="flex flex-col">
        {read.entries.map((entry) => (
          <RosterRow
            key={entry.grant.id}
            entry={entry}
            holder={holderName(entry, ownUserId)}
            role={roleName(entry.grant.role_key, catalog)}
            own={entry.grant.user_id === ownUserId}
            now={read.at}
            busy={busy}
            onRevoke={onRevoke}
          />
        ))}
      </ul>
      <p data-testid="sign-in-note" className="mt-2 text-xs">
        Last sign-in comes from current sessions. A sign-in more than 30 days ago, or one
        ended by signing out, is not on record.
      </p>
    </>
  );
}
